

"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios, { AxiosError } from "axios";
import { AnimatePresence, motion, spring } from "framer-motion";
import { SearchX, ArrowRight } from "lucide-react";
import ProductCard from "./ProductCard";
import FilterPanel from "./FilterPanel";
import {
  FilterState,
  IComingSoonProduct,
  IProduct,
  SearchApiResponse,
} from "../types/types";
import Link from "next/link";
import { useAuth } from "../context/AuthContext";
import {
  ApiResponse,
  ProductByCategory,
  CACHE_DURATION_MINUTES,
} from "../types/types";
import { useDebounce } from "../hooks/useDebounce";
import { ProductGridSkeleton } from "./ProductGridSkeleton";
import { getCachedData, setCachedData } from "../lib/cacheService";
import { PALETTE } from "../lib/colorPallete";
import ExpandedCardView from "./ProductCard/Expandable";
import ComingSoonProductCard from "./ProductCard/ComingSoonProductCard";
import ComingSoonExpandedView from "./ProductCard/ComingSoonExpandedView";

const STUNNING_BLUEPRINT_BG = {
  backgroundColor: PALETTE.darkestGreen,
  backgroundImage: `
        radial-gradient(ellipse at top, rgba(245, 236, 213, 0.08) 0%, transparent 60%),
        url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Cpath d='M0 10 L20 10 M10 0 L10 20' fill='none' stroke='%23F5ECD5' stroke-width='0.5' opacity='0.05'/%3E%3C/pattern%3E%3Cpattern id='big-grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Crect width='100' height='100' fill='url(%23grid)'/%3E%3Cpath d='M0 50 L100 50 M50 0 L50 100' fill='none' stroke='%23F5ECD5' stroke-width='1' opacity='0.07'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23big-grid)' /%3E%3C/svg%3E"),
        linear-gradient(170deg, ${PALETTE.darkerGreen} 0%, ${PALETTE.darkestGreen} 100%)
    `,
};

const isComingSoonProduct = (
  product: IProduct | IComingSoonProduct
): product is IComingSoonProduct => {
  return !("price" in product);
};

const INITIAL_FILTERS: FilterState = {
  query: "",
  category: "",
  minPrice: "",
  maxPrice: "",
  sortBy: "createdAt",
  sortOrder: "desc",
};

const gridContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07 },
  },
};

const categoryTitleVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: spring, stiffness: 100, damping: 15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: spring, stiffness: 100, damping: 20 },
  },
};

interface GroupedData {
  category: string;
  subgroups: {
    subcategory: string;
    products: (IProduct | IComingSoonProduct)[];
  }[];
}

const deduplicateProducts = (
  products: (IProduct | IComingSoonProduct)[]
): (IProduct | IComingSoonProduct)[] => {
  const seen = new Set<string>();
  return products.filter(product => {
    const id = product._id;
    if (!id) {
      return true;
    }
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
};

const ProductGrid: React.FC = () => {
  const [masterProductList, setMasterProductList] = useState<
    (IProduct | IComingSoonProduct)[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeProduct, setActiveProduct] = useState<IProduct | null>(null);
  const [activeComingSoonProduct, setActiveComingSoonProduct] = useState<IComingSoonProduct | null>(null);
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [pagination, setPagination] = useState<
    SearchApiResponse["pagination"] | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();

  const debouncedQuery = useDebounce(filters.query, 500);
  const debouncedMinPrice = useDebounce(filters.minPrice, 800);
  const debouncedMaxPrice = useDebounce(filters.maxPrice, 800);

  const isFilterActive = useMemo(() => {
    return (
      filters.query !== "" ||
      filters.category !== "" ||
      filters.minPrice !== "" ||
      filters.maxPrice !== "" ||
      filters.sortBy !== "createdAt" ||
      filters.sortOrder !== "desc"
    );
  }, [filters]);

  const getCacheKey = useCallback(
    (filterParams: FilterState, page: number = 1) => {
      const params = [];
      if (filterParams.query) params.push(`q-${filterParams.query}`);
      if (filterParams.category) params.push(`cat-${filterParams.category}`);
      if (filterParams.minPrice) params.push(`min-${filterParams.minPrice}`);
      if (filterParams.maxPrice) params.push(`max-${filterParams.maxPrice}`);
      if (filterParams.sortBy !== "createdAt")
        params.push(`sort-${filterParams.sortBy}`);
      if (filterParams.sortOrder !== "desc")
        params.push(`order-${filterParams.sortOrder}`);
      if (user?.role) params.push(`role-${user.role}`);
      if (page > 1) params.push(`page-${page}`);

      return params.length > 0
        ? `api-cache-products-${params.join("-")}`
        : "api-cache-products-initial";
    },
    []
  );

  const fetchProducts = useCallback(
    async (filterParams: FilterState, page: number = 1) => {
      setIsFetching(true);
      setError(null);

      const cacheKey = getCacheKey(filterParams, page);
      const cachedData = getCachedData<{
        products: (IProduct | IComingSoonProduct)[];
        pagination?: SearchApiResponse["pagination"];
      }>(cacheKey, CACHE_DURATION_MINUTES);

      if (cachedData) {
        const dedupedProducts = deduplicateProducts(cachedData.products);
        setMasterProductList(dedupedProducts);
        setPagination(cachedData.pagination || null);
        setIsLoading(false);
        setIsFetching(false);
        return;
      }

      try {
        const hasSearchCriteria =
          filterParams.query ||
          filterParams.category ||
          filterParams.minPrice ||
          filterParams.maxPrice;

        let endpoint: string;
        const params: URLSearchParams = new URLSearchParams();

        if (hasSearchCriteria) {
          endpoint = "/api/products/search";
          if (filterParams.query) params.set("q", filterParams.query);
          if (filterParams.category)
            params.set("category", filterParams.category);
          if (filterParams.minPrice)
            params.set("minPrice", filterParams.minPrice);
          if (filterParams.maxPrice)
            params.set("maxPrice", filterParams.maxPrice);
          params.set("sortBy", filterParams.sortBy);
          params.set("sortOrder", filterParams.sortOrder);
          params.set("limit", "12");
          params.set("page", String(page));
          if (user?.role) {
            params.set("userRole", user.role);
          }
        } else {
          endpoint = "/api/products";
          params.set("limit", "4");
        }

        const fullUrl = `${endpoint}?${params.toString()}`;
        const response = await axios.get<
          SearchApiResponse | ApiResponse<ProductByCategory[]>
        >(fullUrl);

        if (!response.data.success) {
          throw new Error(response.data.error || "Failed to fetch products");
        }

        let products: (IProduct | IComingSoonProduct)[];
        let paginationData: SearchApiResponse["pagination"] | undefined;

        if (hasSearchCriteria) {
          const searchResponse = response.data as SearchApiResponse;
          const allProducts = [
            ...searchResponse.data,
            ...(searchResponse.comingSoonData || []),
          ];
          products = deduplicateProducts(allProducts);
          paginationData = searchResponse.pagination;
        } else {
          const regularResponse = response.data as ApiResponse<
            ProductByCategory[]
          >;
          const allProducts = regularResponse.data.flatMap((cat) => cat.products);
          products = deduplicateProducts(allProducts);
          paginationData = undefined;
        }

        setMasterProductList(products);
        setPagination(paginationData || null);

        setCachedData(cacheKey, { products, pagination: paginationData });
      } catch (err) {
        const axiosError = err as AxiosError<{ error: string }>;
        const errorMessage =
          axiosError.response?.data?.error ||
          (err instanceof Error ? err.message : "An unknown error occurred.");
        setError(errorMessage);
      } finally {
        setIsLoading(false);
        setIsFetching(false);
      }
    },
    [getCacheKey, user?.role]
  );
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedQuery,
    debouncedMinPrice,
    debouncedMaxPrice,
    filters.category,
    filters.sortBy,
    filters.sortOrder,
  ]);
  useEffect(() => {
    const currentFilters = {
      ...filters,
      query: debouncedQuery,
      minPrice: debouncedMinPrice,
      maxPrice: debouncedMaxPrice,
    };
    fetchProducts(currentFilters, currentPage);
  }, [
    currentPage,
    debouncedQuery,
    debouncedMinPrice,
    debouncedMaxPrice,
    filters.category,
    filters.sortBy,
    filters.sortOrder,
    fetchProducts,
    user?.role,
  ]);

  const productsData = useMemo<GroupedData[]>(() => {
    if (!masterProductList || masterProductList.length === 0) return [];

    const categoryMap: Record<
      string,
      Record<string, (IProduct | IComingSoonProduct)[]>
    > = {};

    masterProductList.forEach((product) => {
      const category = product.category;
      const subcategory = product.subcategory || "General";

      if (!categoryMap[category]) {
        categoryMap[category] = {};
      }
      if (!categoryMap[category][subcategory]) {
        categoryMap[category][subcategory] = [];
      }
      categoryMap[category][subcategory].push(product);
    });

    return Object.entries(categoryMap)
      .map(([category, subgroups]) => ({
        category,
        subgroups: Object.entries(subgroups)
          .map(([subcategory, products]) => ({
            subcategory,
            products,
          }))
          .sort((a, b) => a.subcategory.localeCompare(b.subcategory)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [masterProductList]);

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => setFilters(newFilters),
    []
  );

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPagination(null);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const listKey = useMemo(() => {
    return `${currentPage}-${getCacheKey(filters, currentPage)}`;
  }, [currentPage, filters, getCacheKey]);

  if (isLoading)
    return (
      <section className="w-full py-16 sm:py-24" style={STUNNING_BLUEPRINT_BG}>
        <ProductGridSkeleton />
      </section>
    );
  if (error)
    return (
      <section
        className="w-full min-h-[70vh] flex items-center justify-center p-4"
        style={STUNNING_BLUEPRINT_BG}
      >
        <div className="text-center text-red-800 bg-red-100 p-8 rounded-lg shadow-xl border border-red-200">
          <h3 className="text-xl font-bold">An Error Occurred</h3>
          <p className="mt-2 max-w-md">{error}</p>
          <button
            onClick={() => fetchProducts(filters, currentPage)}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </section>
    );

  return (
    <section
      id="featured-products"
      className="w-full py-16 sm:py-24 relative"
      style={STUNNING_BLUEPRINT_BG}
    >
      {isFetching && (
        <div
          className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-amber-400 to-transparent animate-pulse"
          style={{ zIndex: 50 }}
        />
      )}

      <div className="container mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1
            className="font-serif text-4xl sm:text-5xl md:text-6xl font-normal"
            style={{
              color: PALETTE.cream,
              textShadow: "0 3px 15px rgba(0,0,0,0.5)",
            }}
          >
            Engineered for Performance
          </h1>
          <p
            className="mt-4 max-w-xl mx-auto text-lg"
            style={{ color: PALETTE.lightGray }}
          >
            High-quality self-priming pumps, sprayers, and parts built to last.
          </p>
          <div
            className="mt-6 h-1 w-32 mx-auto rounded-full"
            style={{
              backgroundColor: PALETTE.gold,
              boxShadow: "0 0 15px rgba(240, 187, 120, 0.4)",
            }}
          />
        </motion.div>

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          isLoading={isFetching}
        />

        {pagination && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 text-center"
          >
            <p style={{ color: PALETTE.lightGray }}>
              Showing {masterProductList.length} of {pagination.totalCount}{" "}
              products
              {pagination.totalPages > 1 &&
                ` (Page ${pagination.currentPage} of ${pagination.totalPages})`}
            </p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {productsData.length > 0 ? (
            <motion.div
              key={listKey}
              variants={gridContainerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-20"
            >
              {productsData.map((categoryData, catIndex) => (
                <motion.div
                  key={`${categoryData.category}-${catIndex}`}
                  variants={itemVariants}
                >
                  <motion.div
                    variants={categoryTitleVariants}
                    className="mb-10"
                  >
                    <div className="flex items-end justify-between flex-wrap gap-y-2">
                      <h2
                        className="font-serif text-3xl sm:text-4xl capitalize"
                        style={{ color: PALETTE.cream }}
                      >
                        {categoryData.category}
                      </h2>
                      <Link
                        href={`/products/${categoryData.category}`}
                        className="group flex items-center gap-1.5 text-sm font-semibold opacity-70 hover:opacity-100 transition-all"
                        style={{ color: PALETTE.gold }}
                      >
                        See all{" "}
                        <ArrowRight
                          size={16}
                          className="transition-transform group-hover:translate-x-1"
                        />
                      </Link>
                    </div>
                    <div
                      className="mt-2 h-0.5 w-full max-w-lg rounded-full"
                      style={{ backgroundColor: PALETTE.darkerGreen }}
                    />
                  </motion.div>

                  <div className="space-y-12">
                    {categoryData.subgroups.map((subgroup, subIndex) => (
                      <section key={`${subgroup.subcategory}-${subIndex}`}>
                        <h3 className="font-serif text-2xl capitalize text-stone-300 mb-6">
                          {subgroup.subcategory.replace(/-/g, " ")}
                        </h3>
                        <motion.div
                          variants={gridContainerVariants}
                          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8"
                        >
                          {subgroup.products.map((product, prodIndex) => (
                            <motion.div
                              key={`${product._id}-${prodIndex}`}
                              variants={itemVariants}
                            >
                              {isComingSoonProduct(product) ? (
                                <ComingSoonProductCard product={product} onCardClick={setActiveComingSoonProduct} />
                              ) : (
                                <ProductCard
                                  product={product}
                                  onCardClick={setActiveProduct}
                                />
                              )}
                            </motion.div>
                          ))}
                        </motion.div>
                      </section>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="no-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-20"
              style={{ color: PALETTE.lightGray }}
            >
              <SearchX size={48} className="mx-auto opacity-50" />
              <h3
                className="text-2xl font-semibold mt-4"
                style={{ color: PALETTE.cream }}
              >
                {isFilterActive
                  ? "No Equipment Found"
                  : "Inventory is Currently Empty"}
              </h3>
              <p className="mt-2 text-lg">
                {isFilterActive
                  ? "Try adjusting your filters to find what you need."
                  : "Please check back soon for new arrivals."}
              </p>
              {isFilterActive && (
                <button
                  onClick={resetFilters}
                  className="mt-4 px-6 py-2 rounded-md text-white font-semibold transition-colors"
                  style={{ backgroundColor: PALETTE.darkGreen }}
                >
                  Clear All Filters
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {pagination && pagination.totalPages > 1 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-12 flex justify-center items-center gap-2 flex-wrap"
          >
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrevPage || isFetching}
              className="px-4 py-2 rounded-md text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: PALETTE.darkGreen }}
            >
              Previous
            </button>

            <div className="flex gap-1">
              {Array.from(
                { length: Math.min(5, pagination.totalPages) },
                (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={isFetching}
                      className={`px-3 py-2 rounded-md font-semibold transition-all ${currentPage === pageNum
                        ? "text-white shadow-lg"
                        : "text-stone-300 hover:text-white hover:bg-opacity-80"
                        }`}
                      style={{
                        backgroundColor:
                          currentPage === pageNum
                            ? PALETTE.gold
                            : PALETTE.darkerGreen,
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                }
              )}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasNextPage || isFetching}
              className="px-4 py-2 rounded-md text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: PALETTE.darkGreen }}
            >
              Next
            </button>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {activeProduct && (
          <ExpandedCardView
            product={activeProduct}
            onClose={() => setActiveProduct(null)}
            cartItemToEdit={null}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeComingSoonProduct && (
          <ComingSoonExpandedView
            product={activeComingSoonProduct}
            onClose={() => setActiveComingSoonProduct(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProductGrid;